import {
  ChangeEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { Container, Header } from "components/Page"
import {
  MediaConfigScope,
  MediaField,
  MediaType,
} from "../../GlobalMediaTypes/types"
import { Table, createDataSource, useDatasource } from "components/Bable"
import { css, cx } from "@emotion/css"
import {
  deleteField,
  fetchMediaFields,
  fetchMediaFieldsSemantics,
  fetchMediaFieldsTypes,
  fetchMediaTypes,
  patchField,
} from "api/globalMediaTypes"
import { useMutation, useQuery } from "react-query"

import { AddNewField } from "../AddNewField"
import { ApiError } from "api/http/utils"
import { Box } from "@material-ui/core"
import Breadcrumbs from "components/Breadcrumbs"
import Button from "ui/buttons/Button"
import { GLOBAL_MEDIA_FIELDS_COLUMNS } from "./columns"
import { EmptyState } from "components/EmptyState"
import { ReactComponent as FieldsIcon } from "assets/icon-fields.svg"
import { Search } from "../../components/Search"
import Surface from "components/Surface"
import { WithAuthority } from "components/WithAuthority"
import { useAuthenticated } from "providers/A12n"
import { useFeedback } from "components/FeedbackSnackbar"
import { useI18n } from "providers/I18n"
import { getTypeRelatedFieldFormats } from "routes/Base/Plans/Details/utils"
import { escapeRegExp } from "lodash"

const getRowId = (x: MediaField) => x.id

export const globalState = createDataSource({ getRowId })

type Props = {
  configScope?: MediaConfigScope
  configOwnerId?: string
}

const GlobalMediaFields = (props: Props) => {
  const { t } = useI18n()
  const { configScope = "GLOBAL", configOwnerId } = props
  const [open, setOpen] = useState<boolean>(false)
  const sendFeedback = useFeedback((s) => s.send)
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const [searchValue, setSearchValue] = useState<string>("")
  const isModalOpen = Boolean(modalInfo)

  useEffect(() => globalState.setErrors({}), [])

  // ** fetch fields

  const enabled =
    configScope === "GLOBAL" ? !!configScope : !!configOwnerId && !!configScope

  const { data, isLoading, status } = useQuery(
    ["global-media-fields", { configScope, configOwnerId }],
    fetchMediaFields,
    {
      enabled,
      onSuccess: (d: MediaField[]) => {
        globalState.setData(d)
      },
    },
  )
  const noData = !data?.length
  const pending = isLoading || status === "idle"

  // ** fetch media types
  const { data: mediaTypesList } = useQuery(
    ["global-media-types", { configScope, configOwnerId }],
    fetchMediaTypes,
  )

  // ** fetch fields semantics
  const { data: fieldsSemantics } = useQuery(
    ["media-fields-semantics"],
    fetchMediaFieldsSemantics,
  )

  // ** fetch fields types
  const { data: fieldsTypes } = useQuery(
    ["media-fields-types"],
    fetchMediaFieldsTypes,
  )

  // ** remove field
  const [remove] = useMutation(deleteField, {
    onSuccess: (_, id: string) => {
      setModalInfo(undefined)
      globalState.removeRow(id)
    },
    onError: (e) => {
      sendFeedback(t(e.message), "error")
      setModalInfo(undefined)
    },
  })

  // ** patch field inputs
  const [patch] = useMutation(patchField, {
    onSuccess: (d: MediaField) => globalState.updateRow(d),
    onError: (e: ApiError, mf: Partial<MediaField>) => {
      const error = e as ApiError
      const errorMap = error?.errorsMap ?? {}
      !!mf.id && globalState.setRowErrors(mf.id, errorMap)
    },
  })

  const source = useDatasource({
    state: globalState,
    filters: [
      {
        active: !!searchValue.trim(),
        predicate: (row) => {
          const strReg = escapeRegExp(searchValue)
          const reg = new RegExp(strReg, "ig")
          const mdExists = (md: MediaType) => reg.test(md.name || "")
          return row?.mediaTypes?.some(mdExists) || reg.test(row?.name || "")
        },
      },
    ],
  })

  const handleSort = (columnId: string, a: MediaField, b: MediaField) => {
    if (!Boolean(a) || !Boolean(b)) {
      return 0
    }

    switch (columnId) {
      case "name": {
        return a.name.localeCompare(b.name)
      }
      case "semanticCode": {
        return a.semanticName?.localeCompare(b.semanticName)
      }
      case "type": {
        return a.type?.localeCompare(b.type)
      }
      default: {
        return 0
      }
    }
  }

  const handleSearchChange = (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = evt.target
    setSearchValue(() => value)
  }

  const handleSearchClear = () => {
    setSearchValue(() => "")
  }

  const handleDelete = useCallback(
    (row: MediaField) => {
      setModalInfo({
        title: t("admin.fields.delete.title"),
        type: "primary_destructive",
        description: t("admin.fields.delete.description"),
        submitLabel: t("admin.fields.delete.action"),
        onSubmit: async () => await remove(row?.id),
      })
    },
    [remove, t],
  )

  const handleClick = () => setOpen(true)

  const onClose = () => setOpen(false)

  const onUpdate = useCallback(
    async (row, label, value) => {
      patch({ id: row.id, [label]: value })
    },
    [patch],
  )

  const hasMediaTypes = (row?: MediaField) =>
    !!row && !!row.mediaTypes && !!row.mediaTypes.length

  const semanticsMapped = useMemo(
    () => fieldsSemantics?.map((s) => ({ label: s.name, value: s.code })) || [],
    [fieldsSemantics],
  )

  const fieldTypesMapped = useMemo(
    () => fieldsTypes?.map((ft) => ({ value: ft, label: ft })) || [],
    [fieldsTypes],
  )

  const filteredTypes = useCallback(
    (row: MediaField) =>
      fieldsSemantics?.filter((s) => s?.code === row?.semanticCode) || [],
    [fieldsSemantics],
  )

  const typesMapped = useCallback(
    (row: MediaField) =>
      !!filteredTypes(row)?.length && !!filteredTypes(row)[0].type.length
        ? filteredTypes(row)[0].type?.map((t: string) => ({
            value: t,
            label: t,
          }))
        : fieldTypesMapped,
    [fieldTypesMapped, filteredTypes],
  )

  const fieldFormatsPerSemantic = useCallback(
    (row: MediaField) => {
      if (!!row?.semanticCode) {
        return (
          fieldsSemantics?.find((s) => s?.code === row?.semanticCode)?.format ||
          []
        )
      } else if (!!row?.type) {
        return getTypeRelatedFieldFormats(row.type)
      } else {
        return []
      }
    },
    [fieldsSemantics],
  )

  const fieldFormatsMapped = useCallback(
    (row: MediaField) =>
      fieldFormatsPerSemantic(row)?.map((f) => ({
        value: f,
        label: f,
      })),
    [fieldFormatsPerSemantic],
  )

  const listOptions = (row: MediaField) => row?.options

  const formulas = (row: MediaField) => row?.formulaSet?.formulaParts || []

  const context = useMemo(
    () => ({
      semanticsOptions: semanticsMapped,
      typesOptions: typesMapped,
      formatsOptions: fieldFormatsMapped,
      mediaTypesList,
      listOptions,
      formulas,
      onUpdate,
      onDelete: handleDelete,
      disabled: hasMediaTypes,
      configScope,
      configOwnerId,
    }),
    [
      handleDelete,
      mediaTypesList,
      onUpdate,
      semanticsMapped,
      typesMapped,
      fieldFormatsMapped,
      configScope,
      configOwnerId,
    ],
  )

  const globalSurface = configScope === "GLOBAL" || configScope === "MARKET"
  const { hasAuthority } = useAuthenticated()
  const actionAllowed = hasAuthority(["AUTH_DELETE_MEDIA_FIELD"])

  return (
    <GlobalMediaFieldsContextOptions.Provider value={context}>
      <Container className={cx({ [styles.container]: !globalSurface })}>
        {globalSurface ? (
          <>
            <Breadcrumbs
              section={t("nav.admin")}
              current={t("nav.admin.media-fields")}
            />
            <Header label={t("nav.admin.media-fields")}>
              <WithAuthority allowed={["AUTH_CREATE_MEDIA_FIELD"]}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleClick}
                  disabled={open}
                >
                  {t("admin.mediaFields.add_new")}
                </Button>
              </WithAuthority>
            </Header>
          </>
        ) : (
          <></>
        )}
        <Surface
          className={cx(styles.wrappedSurface, {
            [styles.surface]: !globalSurface,
          })}
          flexDirection="row"
        >
          {open ? (
            <AddNewField
              onClose={onClose}
              isLoading={isLoading}
              configScope={configScope}
              configOwnerId={configOwnerId}
            />
          ) : null}
          <div
            className={cx(styles.tableToolbar, {
              [styles.noDataTableToolbar]: noData,
            })}
          >
            <div className="left">
              {!noData ? (
                <Search
                  name={"name"}
                  value={searchValue}
                  handleChange={handleSearchChange}
                  handleClear={handleSearchClear}
                  placeholder={t("inputs.search.placeholder")}
                />
              ) : null}
            </div>
            {configScope !== "GLOBAL" && configScope !== "MARKET" ? (
              <Box display="flex" justifyContent="flex-end">
                <WithAuthority allowed={["AUTH_CREATE_MEDIA_FIELD"]}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleClick}
                    disabled={open}
                    className={cx(styles.btn, { [styles.noDataBtn]: noData })}
                  >
                    {t("admin.mediaFields.add_new")}
                  </Button>
                </WithAuthority>
              </Box>
            ) : null}
          </div>
          <WithAuthority allowed={["AUTH_VIEW_MEDIA_FIELD"]}>
            <Table<MediaField>
              columns={GLOBAL_MEDIA_FIELDS_COLUMNS(actionAllowed)}
              source={source}
              height={650}
              width="initial"
              className={styles.table}
              pending={pending}
              emptyText={
                <EmptyState
                  title={t("media.fields.noresults.title")}
                  icon={<FieldsIcon />}
                  subtitle={
                    hasAuthority(["AUTH_CREATE_MEDIA_FIELD"])
                      ? t("media.fields.noresults.subtitle")
                      : undefined
                  }
                >
                  {hasAuthority(["AUTH_CREATE_MEDIA_FIELD"]) ? (
                    <span
                      onClick={handleClick}
                      className={cx(
                        "BodyBody-1Primary-mainRegular",
                        styles.createNew,
                      )}
                    >
                      {t("admin.mediaFields.add_new_subtitle")}
                    </span>
                  ) : null}
                </EmptyState>
              }
              onSort={handleSort}
            />
          </WithAuthority>
          <ConfirmationModal
            open={isModalOpen}
            onCancel={() => setModalInfo(undefined)}
            {...modalInfo}
          />
        </Surface>
      </Container>
    </GlobalMediaFieldsContextOptions.Provider>
  )
}

export default GlobalMediaFields

export const GlobalMediaFieldsContextOptions = createContext<any | undefined>(
  undefined,
)

export const useContextOptions = () => {
  const options = useContext(GlobalMediaFieldsContextOptions)

  if (options === undefined) {
    throw new Error("error")
  }
  return options
}

const styles = {
  tableToolbar: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0;
    padding: 0 0 var(--spacing-xxl-2);

    .left {
      display: flex;
      align-items: center;
      & > :not(:first-child) {
        margin-left: var(--spacing-m);
      }
    }
  `,
  noDataTableToolbar: css`
    padding: 0;
  `,
  table: css`
    .RebaseTable__header-cell {
      padding: 0 12px;
      font-size: 12px;
      color: var(--black);
      font-weight: bold;
      text-transform: capitalize;
    }
    .RebaseTable__grid-cell {
    }

    .RebaseTable__body-row:hover {
      background-color: var(--primary-light);
    }

    .RebaseTable__body-row--hovered {
      background-color: var(--primary-light);
    }

    .RebaseTable__cell {
      &[data-column-id^="__"] {
        border-right: none;
        padding: 0;
      }
      :last-child {
        display: flex;
        justify-content: center;
      }
    }
    .RebaseTable__cell:focus-within {
      box-shadow: none;
    }
  `,
  container: css`
    padding: 0px;
  `,
  surface: css`
    padding: var(--spacing-xxl-2) 0 0;
  `,
  wrappedSurface: css`
    padding: var(--spacing-xxl-2) var(--spacing-m) var(--spacing-m);
  `,
  createNew: css`
    padding-top: 1px;
    margin: 0 0.8ch;
    cursor: pointer;
  `,
  btn: css`
    margin: 0px;
  `,
  noDataBtn: css`
    margin: 0px 0px var(--spacing-xxl-2);
  `,
}
